import { useQuery } from '@tanstack/react-query';

import { useWorkflowDetail } from '@/hooks/queries/workflow/use-workflow-detail';
import { listWorkflowAccess } from '@/services/workflow/share-workflow';
import { useOrgStore } from '@/stores/org-store';
import { hasClientDataLabel, isSharedPublicly } from '@/utils/workflow-summary';

/** The data half of a `WorkflowCard`, resolved from a workflow id. */
export interface WorkflowSummary {
  name: string;
  description?: string;
  isPublic: boolean;
  nodeCount?: number;
  lastRunAt?: string | null;
  clientData: boolean;
  collaborators: string[];
}

export interface UseWorkflowSummaryResult {
  isLoading: boolean;
  summary?: WorkflowSummary;
  /** Cost of the workflow's most recent run, when the backend has a figure. */
  latestRunCostUsd?: number | null;
}

/** First of `values` that carries text, so a blank field falls through. */
function firstText(
  ...values: (string | null | undefined)[]
): string | undefined {
  for (const value of values) {
    const text = value?.trim();
    if (text) return text;
  }
  return undefined;
}

/**
 * Resolves a workflow into summary-card data. The workflows grid builds the
 * same shape from rows it already has; anywhere that starts from a bare id
 * (agent chat) goes through here so both stay in step.
 */
export function useWorkflowSummary(
  workflowId?: string,
  { includeCost = false }: { includeCost?: boolean } = {},
): UseWorkflowSummaryResult {
  const orgId = useOrgStore((state) => state.currentOrg?.orgid);
  const enabled = !!orgId && !!workflowId;

  const detailQuery = useWorkflowDetail(workflowId, { includeCost });

  const accessQuery = useQuery({
    queryKey: ['workflow-access', orgId, workflowId],
    queryFn: () => listWorkflowAccess(orgId!, workflowId!),
    enabled,
    staleTime: 60_000,
    retry: 1,
  });

  const detail = detailQuery.data?.data;
  if (!detail) return { isLoading: enabled && detailQuery.isLoading };

  const inner = detail.workflow;
  // The flat `name`/`description` fields are the source of truth and win
  // when present; the saved payload's copies are only a fallback for
  // workflows fetched through an endpoint that doesn't return the flat
  // fields yet, and go stale on rename.
  const payload = inner?.payload;
  return {
    isLoading: false,
    latestRunCostUsd: detail.latest_run_cost_usd,
    summary: {
      name:
        firstText(detail.name, inner?.name, payload?.name) ??
        'Untitled Workflow',
      description: firstText(
        detail.description,
        inner?.description,
        payload?.description,
      ),
      isPublic: isSharedPublicly({
        default_role: inner?.default_role,
        publish_to_template:
          detail.publish_to_template ?? payload?.publish_to_template,
      }),
      nodeCount: detail.node_count ?? payload?.nodes?.length,
      lastRunAt: detail.last_run_at ?? payload?.lastExecution?.timestamp,
      clientData: hasClientDataLabel(inner?.labels),
      collaborators: (accessQuery.data?.access ?? [])
        .map((entry) => entry.email)
        .filter((email): email is string => !!email),
    },
  };
}
